import { attach, createEvent, createStore } from 'effector';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as slug from 'limax';
import { generateUuid } from '@lib/uuid';
import {
  $menuState,
  generateNoteMenuItem,
  generateNotesFolderMenuItem,
  getMenuSlugs,
  getMenuItemsByIds,
  generatePageMenuItem,
  PortalFilesDashboardSlug,
  PortalChatsDashboardSlug,
  PortalTasksDashboardSlug,
} from '@nx-frontend/shared/store/feature-menu-tree';
import {
  AllowAllPermissions,
  ContentBlock,
  GeneratePageParams,
  GenerateSectionParams,
  MenuItem,
  MenuItemType,
  MenuType,
  PortalBlockType,
  PortalNodeType,
  VirtualPortalFolder,
  VirtualPortalNode,
  VirtualPortalNote,
  VirtualPortalPage,
  VirtualPortalSection,
} from '@nx-frontend/shared/types';
import { MenusState } from '@nx-frontend/shared/types';
import { getWrapperProps } from '@nx-frontend/shared/utils';
import { getUniqueUrl } from '@nx-frontend/shared/utils';
import { setVirtualPathsCookie } from './virtual-portal-node-utils';

export const createVirtualNode = createEvent<{ type: PortalNodeType }>();
export const addVirtualNode = createEvent<VirtualPortalNode>();
export const deleteVirtualNode = createEvent<VirtualPortalNode>();
export const deleteVirtualNodesEvent = createEvent<string[]>();
export const updateVirtualNodePayloadEvent = createEvent<VirtualNodePayload>();
export const resetVirtualNodePayloadEvent = createEvent();

export interface CreatePagesParams {
  ids: string[];
  menuType: MenuType;
  parentId?: string;
  orgId: string;
  workspaceId: string;
}

export interface VirtualPortalNodeState {
  entities: Record<string, VirtualPortalNode>;
  ids: string[];
}

const initialVirtualPortalNodeState: VirtualPortalNodeState = {
  entities: {},
  ids: [],
};

export interface VirtualNodePayload {
  parentId?: string;
  menuType: MenuType;
  visible?: boolean;
}

type PageContentBlock = ContentBlock & { attr: { pageId: string; pageTitle: string; pagePath: string } };

export interface VirtualNodePayloadState {
  payload: VirtualNodePayload | null;
}

const initialVirtualNodePayloadState: VirtualNodePayloadState = {
  payload: null,
};

export const createPagesFromNimbusContentFx = attach({
  source: [$menuState],
  effect: ([menuState]: [MenusState], { ids, menuType, parentId, orgId, workspaceId }: CreatePagesParams) => {
    const existedSlugs = getMenuSlugs(menuState, [
      MenuType.PORTAL_TOPBAR,
      MenuType.PORTAL_SIDEBAR,
      MenuType.PORTAL_FOOTER,
    ]);
    const parentItem = parentId ? getMenuItemsByIds(menuState, menuType, [parentId])[0] : null;

    return generateNodesAndMenuItems(menuState, menuType, ids, existedSlugs, parentItem, orgId, workspaceId);
  },
});

function generateNodesAndMenuItems(
  state: MenusState,
  menuType: MenuType,
  ids: string[],
  existedPaths: string[],
  parentItem: MenuItem | null,
  orgId: string,
  workspaceId: string,
  isLoop?: boolean
): { nodes: VirtualPortalNode[]; menuItems: MenuItem[]; blocks: PageContentBlock[] } {
  const tempExistedPaths = existedPaths;
  const targetMenuItems = getMenuItemsByIds(state, MenuType.NIMBUS_CONTENT, ids);
  const order = parentItem?.order ?? state[menuType].rootOrder;

  return targetMenuItems
    .sort((a, b) => a.index - b.index)
    .reduce(
      (acc, item, currentIndex) => {
        const node = generateNodeFromMenuItem(item, parentItem, tempExistedPaths);
        const index = isLoop ? currentIndex : order.length + currentIndex + 1;
        let menuItem: MenuItem | null = null;
        if (item.type === MenuItemType.NOTE) {
          const noteId = item.id.split('#')[1];

          menuItem = generateNoteMenuItem({
            parentId: parentItem?.id,
            name: node.title,
            url: node.path,
            openInNewTab: false,
            targetId: noteId,
            pageId: node.id,
            index,
          });

          const block: PageContentBlock = {
            id: generateUuid(),
            type: PortalBlockType.CONTENT,
            title: menuItem.name,
            text: '',
            orgId,
            workspaceId,
            noteId,
            index: 0,
            place: 'page_body',
            colspan: 6,
            rowspan: 6,
            actions: [],
            wrapper: getWrapperProps('blank'),
            mediaSizes: ['base', 'md', 'lg'],
            enabled: true,
            removable: true,
            pageId: node.id,
            data: [],
            attr: {
              pageId: node.id,
              pageTitle: node.title,
              pagePath: node.path,
            },
          };
          acc.blocks.push(block);
        } else if (item.type === MenuItemType.NOTES_FOLDER) {
          const folderId = item.id.split('#')[1];
          menuItem = generateNotesFolderMenuItem({
            parentId: parentItem?.id,
            name: node.title,
            url: node.path,
            targetId: folderId,
            openInNewTab: false,
            pageId: node.id,
            index,
          });
        }

        if (menuItem) {
          tempExistedPaths.push(menuItem.slug);
          acc.nodes.push(node);
          acc.menuItems.push(menuItem);
          if (item.type === MenuItemType.NOTES_FOLDER && item.order?.length) {
            const { nodes, menuItems, blocks } = generateNodesAndMenuItems(
              state,
              menuType,
              item.order,
              tempExistedPaths,
              menuItem,
              orgId,
              workspaceId,
              true
            );
            acc.nodes.push(...nodes);
            acc.menuItems.push(...menuItems);
            acc.blocks.push(...blocks);
          }
        }
        return acc;
      },
      { nodes: [], menuItems: [], blocks: [] } as {
        nodes: VirtualPortalNode[];
        menuItems: MenuItem[];
        blocks: PageContentBlock[];
      }
    );
}

export const generateSectionFx = attach({
  source: [$menuState],
  effect: (
    [menuState]: [MenusState],
    { title, parentId, menuType, visible }: GenerateSectionParams
  ): VirtualPortalSection => {
    const existedSlugs = getMenuSlugs(menuState, [
      MenuType.PORTAL_TOPBAR,
      MenuType.PORTAL_SIDEBAR,
      MenuType.PORTAL_FOOTER,
    ]);
    const entities = menuState[menuType].entities;
    const parentItem = entities && parentId ? entities[parentId] : null;
    const url = generateUrl(title, existedSlugs, parentItem?.url);
    const id = generateUuid();
    return {
      type: PortalNodeType.SECTION,
      title: title,
      path: url,
      visible: visible === undefined ? true : visible,
      id,
      permissions: AllowAllPermissions,
    };
  },
});

export const generatePageFx = attach({
  source: [$menuState],
  effect: (
    [menuState]: [MenusState],
    { title, parentId, menuType, visible = true }: GeneratePageParams
  ): { node: VirtualPortalPage; menuItem: MenuItem } => {
    const existedSlugs = getMenuSlugs(menuState, [
      MenuType.PORTAL_TOPBAR,
      MenuType.PORTAL_SIDEBAR,
      MenuType.PORTAL_FOOTER,
    ]);
    const menu = menuState[menuType];
    const entities = menu.entities;
    const parentItem = entities && parentId ? entities[parentId] : null;
    const pageTitle = title ?? 'New page';

    const url = generateUrl(pageTitle, existedSlugs, parentItem?.url);
    const uniqueUrl = getUniqueUrl(url, existedSlugs);
    const nodeId = generateUuid();

    const getMenuItemIndex = () => {
      if (parentItem) {
        return parentItem.order?.length ?? 0;
      }
      return menu.rootOrder.length;
    };
    return {
      node: {
        type: PortalNodeType.PAGE,
        title: pageTitle,
        path: uniqueUrl,
        id: nodeId,
        visible,
        permissions: AllowAllPermissions,
      },
      menuItem: generatePageMenuItem({
        parentId,
        name: pageTitle,
        url: uniqueUrl,
        openInNewTab: false,
        pageId: nodeId,
        index: getMenuItemIndex(),
        visible,
      }),
    };
  },
});

export const $virtualPortalNodes = createStore(initialVirtualPortalNodeState)
  .on(addVirtualNode, (state, virtualNode) => {
    const entities = { ...state.entities, ...{ [virtualNode.id]: virtualNode } };
    setVirtualPathsCookie(Object.values(entities));
    return { ...state, entities, ids: Object.keys(entities) };
  })
  .on(deleteVirtualNodesEvent, (state, ids) => {
    const entities = { ...state.entities };
    ids.forEach((id) => {
      delete entities[id];
    });
    setVirtualPathsCookie(Object.values(entities));
    return { ...state, entities, ids: Object.keys(entities) };
  })
  .on(deleteVirtualNode, (state, virtualNode) => {
    const entities = { ...state.entities };
    delete entities[virtualNode.id];
    setVirtualPathsCookie(Object.values(entities));
    return { ...state, entities, ids: Object.keys(entities) };
  });
export const $virtualPortalNodePaths = $virtualPortalNodes.map((state) =>
  Object.values(state.entities).map((node) => node.path)
);

export const $virtualNodePayloadStore = createStore(initialVirtualNodePayloadState)
  .on(updateVirtualNodePayloadEvent, (state, payload) => {
    return { ...state, payload };
  })
  .on(resetVirtualNodePayloadEvent, (state) => {
    return { ...state, payload: null };
  });

export const $virtualNodePayload = $virtualNodePayloadStore.map((state) => state.payload);

export function generateNodeFromMenuItem(
  menuItem: MenuItem,
  parentItem: MenuItem | null,
  existedSlugs: string[]
): VirtualPortalNote | VirtualPortalFolder {
  const title = menuItem.name;
  const id = generateUuid();
  const url = generateUrl(title, existedSlugs, parentItem?.url);

  if (menuItem.type === MenuItemType.NOTE) {
    return {
      type: PortalNodeType.NOTE,
      id,
      title,
      path: url,
      visible: true,
      noteId: 'noteId',
      share: 'shareUrl',
      permissions: AllowAllPermissions,
    };
  } else if (menuItem.type === MenuItemType.NOTES_FOLDER) {
    return {
      type: PortalNodeType.FOLDER,
      title,
      path: url,
      visible: true,
      id,
      permissions: AllowAllPermissions,
    };
  }
  throw new Error(`Unsupported menu item type: ${menuItem.name}-${menuItem.type}`);
}

export function generateUrl(title: string, existedSlugs: string[], parentUrl?: string) {
  const preparedSlug = slug(title);
  const newPreparedSlug = getUniqueUrl(preparedSlug, existedSlugs);
  return (parentUrl ? parentUrl : '') + '/' + newPreparedSlug;
}

export function getFilesPageNode(): VirtualPortalPage {
  return {
    type: PortalNodeType.PAGE,
    title: 'Files',
    path: `/${PortalFilesDashboardSlug}`,
    id: generateUuid(),
    visible: true,
    permissions: AllowAllPermissions,
  };
}

export function getChatsDashboardPageNode(): VirtualPortalPage {
  return {
    type: PortalNodeType.PAGE,
    title: 'Chats',
    path: `/${PortalChatsDashboardSlug}`,
    id: generateUuid(),
    visible: true,
    permissions: AllowAllPermissions,
  };
}

export function getTasksPageNode(): VirtualPortalPage {
  return {
    type: PortalNodeType.PAGE,
    title: 'Task Dashboard',
    path: `/${PortalTasksDashboardSlug}`,
    id: generateUuid(),
    visible: true,
    permissions: AllowAllPermissions,
  };
}
